import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
    Modal,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    CustomInput,
    Spinner,
    Alert,
    Badge
} from 'reactstrap'
import Select from 'react-select'
import { Editor } from 'react-draft-wysiwyg'
import draftToHtml from 'draftjs-to-html'
import { convertToRaw } from 'draft-js'
import CustomAvatar from '@src/views/components/CustomAvatar'
import {
    Send,
    Users,
    UserCheck,
    Globe,
    Folder,
    Filter,
    Sliders,
    Heart,
    ArrowLeftCircle,
    Check,
    X,
    RotateCcw,
    MapPin,
    Award,
    TrendingUp,
    Bookmark,
    Info
} from 'react-feather'
import { HiChatBubbleLeftRight } from 'react-icons/hi2'
import classnames from 'classnames'

// Filter Options matching Nutrition Experts specification
export const REACH_OPTIONS = [
    { value: '0–5K', label: '0 – 5K' },
    { value: '5K–10K', label: '5K – 10K' },
    { value: '10K–50K', label: '10K – 50K' },
    { value: '50K - 100K', label: '50K – 100K' },
    { value: '100,001–500,000', label: '100K – 500K' },
    { value: '500,001+', label: '500K+' }
]

export const YEARS_IN_PRACTICE_OPTIONS = [
    { value: 'Emerging RD (0–2 years)', label: 'Emerging RD (0–2 years)' },
    { value: 'Early Career (3–5 years)', label: 'Early Career (3–5 years)' },
    { value: 'Mid-Career (6–10 years)', label: 'Mid-Career (6–10 years)' },
    { value: 'Seasoned RD (11–20 years)', label: 'Seasoned RD (11–20 years)' },
    { value: 'Veteran RD (21+ years)', label: 'Veteran RD (21+ years)' }
]

const MassMessageModal = ({
    isOpen,
    toggle,
    broadcastMessage,
    setBroadcastMessage,
    massMsgEditorState,
    setMassMsgEditorState,
    selectedBroadcastContacts,
    setSelectedBraodcastContacts,
    selectedRds,
    setSelectedRds,
    broadCastRDsList,
    setBroadCastRDsList,
    broadCastRDsListLoading,
    setBroadCastRDsListLoading,
    sendBroadcastMessage,
    broadCastSendLoader,
    showPreviewModal,
    setShowPreviewModal,
    broadCastResStatus,
    broadCastResMsg,
    brandsDashboardStoreSlice,
    rdGroupListLoading,
    favFolderList,
    setFavFolderList,
    showFav,
    setShowFav,
    backToFav,
    setBackToFav,
    favFolderTitle,
    setFavFolderTitle,
    handleSelectedFolder,
    getRdList,
    selectedRadio,
    setSelectedRadio,
    selectedGroupLabels,
    setSelectedGroupLabels,
    groupData,
    setGroupData,
    checkAllVal,
    setCheckAllVal,
    handleCheckAll,
    handleSelectBroadCastingContact,
    setIsOpenTempleteModal,
    setOpenTemplatesModal,
    setTemplateType,
    dispatch,
    rdGroupList,
    rdGroupListReset
}) => {
    // Missing Filters local state
    const [selectedCountries, setSelectedCountries] = useState([])
    const [selectedStates, setSelectedStates] = useState([])
    const [selectedReach, setSelectedReach] = useState(null)
    const [selectedYearsInPractice, setSelectedYearsInPractice] = useState(null)

    // In-modal search query for recipients
    const [searchRecipientTerm, setSearchRecipientTerm] = useState('')

    // Category filter search terms
    const [specialitySearch, setSpecialitySearch] = useState('')
    const [industrySearch, setIndustrySearch] = useState('')
    const [charSearch, setCharSearch] = useState('')

    // Country & State options from Redux
    const countryOptions = brandsDashboardStoreSlice?.countries || []
    const allStates = brandsDashboardStoreSlice?.statesByRegionID || []

    // Filter states based on selected countries
    const filteredStateOptions = useMemo(() => {
        if (!selectedCountries || selectedCountries.length === 0) {
            return allStates
        }
        const countryIds = selectedCountries.map(c => c.id)
        return allStates.filter(s => countryIds.includes(s.country))
    }, [selectedCountries, allStates])

    // Filter goals attributes based on local search terms
    const filteredSpecialities = useMemo(() => {
        const list = brandsDashboardStoreSlice?.goalsAttributes?.speciality || []
        if (!specialitySearch.trim()) return list
        return list.filter(item => (item.label || '').toLowerCase().includes(specialitySearch.toLowerCase()))
    }, [brandsDashboardStoreSlice?.goalsAttributes?.speciality, specialitySearch])

    const filteredIndustries = useMemo(() => {
        const list = brandsDashboardStoreSlice?.goalsAttributes?.industry || []
        if (!industrySearch.trim()) return list
        return list.filter(item => (item.label || '').toLowerCase().includes(industrySearch.toLowerCase()))
    }, [brandsDashboardStoreSlice?.goalsAttributes?.industry, industrySearch])

    const filteredCharacteristics = useMemo(() => {
        const list = brandsDashboardStoreSlice?.goalsAttributes?.personal_attributes || []
        if (!charSearch.trim()) return list
        return list.filter(item => (item.label || '').toLowerCase().includes(charSearch.toLowerCase()))
    }, [brandsDashboardStoreSlice?.goalsAttributes?.personal_attributes, charSearch])

    // Compute active filter count
    const activeFilterCount = useMemo(() => {
        let count = 0
        if (groupData?.speciality?.length > 0) count += groupData.speciality.length
        if (groupData?.industry?.length > 0) count += groupData.industry.length
        if (groupData?.characteristics?.length > 0) count += groupData.characteristics.length
        if (selectedCountries?.length > 0) count += selectedCountries.length
        if (selectedStates?.length > 0) count += selectedStates.length
        if (selectedReach) count += 1
        if (selectedYearsInPractice) count += 1
        return count
    }, [groupData, selectedCountries, selectedStates, selectedReach, selectedYearsInPractice])

    // Helper: update group filter queries (including location, reach, years in practice)
    const triggerGroupSearch = useCallback((overrides = {}) => {
        const currentGroupData = overrides.groupData !== undefined ? overrides.groupData : groupData
        const currentCountries = overrides.countries !== undefined ? overrides.countries : selectedCountries
        const currentStates = overrides.states !== undefined ? overrides.states : selectedStates
        const currentReach = overrides.reach !== undefined ? overrides.reach : selectedReach
        const currentYears = overrides.years !== undefined ? overrides.years : selectedYearsInPractice

        const hasAnyFilter = (
            (currentGroupData?.speciality?.length > 0) ||
            (currentGroupData?.industry?.length > 0) ||
            (currentGroupData?.characteristics?.length > 0) ||
            (currentCountries?.length > 0) ||
            (currentStates?.length > 0) ||
            Boolean(currentReach) ||
            Boolean(currentYears)
        )

        if (!hasAnyFilter) {
            dispatch(rdGroupListReset())
            setBroadCastRDsList([])
            setSelectedBraodcastContacts([])
            setSelectedRds([])
            return
        }

        const payload = {
            filter: 'list'
        }

        if (currentGroupData?.speciality?.length > 0) {
            payload.speciality = currentGroupData.speciality
        }
        if (currentGroupData?.industry?.length > 0) {
            payload.industry = currentGroupData.industry
        }
        if (currentGroupData?.characteristics?.length > 0) {
            payload.personal_attr = currentGroupData.characteristics
        }
        if (currentCountries?.length > 0) {
            payload.country = currentCountries.map(c => c.id)
        }
        if (currentStates?.length > 0) {
            payload.state = currentStates.map(s => s.id)
        }
        if (currentReach?.value) {
            payload.followers = currentReach.value
        }
        if (currentYears?.value) {
            payload.years_in_practice = currentYears.value
        }

        dispatch(rdGroupList(payload))
    }, [groupData, selectedCountries, selectedStates, selectedReach, selectedYearsInPractice, dispatch, rdGroupList, rdGroupListReset, setBroadCastRDsList, setSelectedBraodcastContacts, setSelectedRds])

    // Checkbox toggle for group categories
    const handleGroupCheckboxToggle = (category, event, value) => {
        dispatch(rdGroupListReset())
        setBroadCastRDsList([])
        setSelectedBraodcastContacts([])
        setSelectedRds([])

        const label = value?.label || ''
        if (label) {
            setSelectedGroupLabels(prev => {
                if (event.target.checked) {
                    return [...prev.filter(l => l !== label), label]
                } else {
                    return prev.filter(l => l !== label)
                }
            })
        }

        const currentList = groupData[category] || []
        const isChecked = event.target.checked
        const updatedList = isChecked
            ? [...currentList.filter(id => id !== value.id), value.id]
            : currentList.filter(id => id !== value.id)

        const updatedGroupData = {
            ...groupData,
            [category]: updatedList
        }

        setGroupData(updatedGroupData)
        triggerGroupSearch({ groupData: updatedGroupData })
    }

    // Reset all filters in groups mode
    const handleResetAllFilters = () => {
        setGroupData({ speciality: [], industry: [], characteristics: [] })
        setSelectedCountries([])
        setSelectedStates([])
        setSelectedReach(null)
        setSelectedYearsInPractice(null)
        setSelectedGroupLabels([])
        dispatch(rdGroupListReset())
        setBroadCastRDsList([])
        setSelectedBraodcastContacts([])
        setSelectedRds([])
    }

    // Filter and sort recipients alphabetically by name (A-Z)
    const displayRecipients = useMemo(() => {
        const sorted = (broadCastRDsList || []).slice().sort((a, b) => {
            const nameA = (a?.name || a?.rd_name || '').trim().toLowerCase()
            const nameB = (b?.name || b?.rd_name || '').trim().toLowerCase()
            return nameA.localeCompare(nameB)
        })
        if (!searchRecipientTerm.trim()) {
            return sorted
        }
        const term = searchRecipientTerm.toLowerCase().trim()
        return sorted.filter(item => {
            const name = (item.name || item.rd_name || '').toLowerCase()
            return name.includes(term)
        })
    }, [broadCastRDsList, searchRecipientTerm])

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} className="mass-msg-modal" backdrop="static">
                {/* Modern Modal Header */}
                <div className="mass-msg-header d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="mass-msg-icon-badge mr-1 flex-shrink-0">
                            <HiChatBubbleLeftRight size={22} />
                        </div>
                        <div>
                            <h5 className="mb-0 font-weight-bold" style={{ color: '#1e293b', fontSize: '17px' }}>
                                Mass Message Broadcast
                            </h5>
                            <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                Reach multiple Registered Dietitians at once. Direct replies will appear in your Inbox.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="mass-msg-close-btn"
                        onClick={toggle}
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <ModalBody className="mass-msg-body p-2">
                    {/* Section 1: Compose Message */}
                    <div className="mass-msg-card mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-1 pb-50 border-bottom">
                            <div className="d-flex align-items-center">
                                <Bookmark size={16} className="text-primary mr-50" />
                                <h6 className="mb-0 font-weight-bold" style={{ fontSize: '13.5px', color: '#1e293b' }}>
                                    1. Compose Message
                                </h6>
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm mass-template-btn"
                                    onClick={() => {
                                        setIsOpenTempleteModal(false)
                                        setOpenTemplatesModal(true)
                                        setTemplateType('mass')
                                    }}
                                >
                                    Browse Templates
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm mass-template-save-btn"
                                    disabled={!broadcastMessage || broadcastMessage.trim() === ''}
                                    onClick={() => {
                                        setOpenTemplatesModal(false)
                                        setIsOpenTempleteModal(true)
                                        setTemplateType('mass')
                                    }}
                                >
                                    Save as Template
                                </button>
                            </div>
                        </div>

                        <div className="mass-editor-wrapper">
                            <Editor
                                editorState={massMsgEditorState}
                                onEditorStateChange={(e) => {
                                    setMassMsgEditorState(e)
                                    const contentState = e.getCurrentContent()
                                    if (contentState.hasText()) {
                                        setBroadcastMessage(draftToHtml(convertToRaw(contentState)))
                                    } else {
                                        setBroadcastMessage('')
                                    }
                                }}
                                toolbar={{
                                    options: ['inline', 'list', 'link', 'image'],
                                    inline: {
                                        options: ['bold', 'italic', 'underline']
                                    },
                                    list: {
                                        options: ['unordered', 'ordered']
                                    },
                                    link: {
                                        popupClassName: 'msg-editor-link-and-img',
                                        showOpenOptionOnHover: false
                                    },
                                    image: {
                                        popupClassName: 'msg-editor-link-and-img',
                                        defaultSize: {
                                            height: '400',
                                            width: '400'
                                        }
                                    }
                                }}
                                editorClassName="mass-msg-editor-area"
                                stripPastedStyles={true}
                                placeholder="Type your broadcast message here..."
                            />
                        </div>
                        <small className="text-muted d-block mt-50" style={{ fontSize: '11.5px' }}>
                            Dietitians will receive this broadcast as a personalized individual message in their chat thread.
                        </small>
                    </div>

                    {/* Section 2: Audience Selection (Segmented Pill Switcher) */}
                    <div className="mass-msg-card mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-1 pb-50 border-bottom">
                            <div className="d-flex align-items-center">
                                <Users size={16} className="text-primary mr-50" />
                                <h6 className="mb-0 font-weight-bold" style={{ fontSize: '13.5px', color: '#1e293b' }}>
                                    2. Target Audience
                                </h6>
                            </div>
                            {selectedRadio === 'groups' && activeFilterCount > 0 && (
                                <Badge color="light-primary" pill style={{ fontSize: '11px', padding: '4px 10px' }}>
                                    {activeFilterCount} Active {activeFilterCount === 1 ? 'Filter' : 'Filters'}
                                </Badge>
                            )}
                        </div>

                        {/* Modern Segmented Pill Tabs */}
                        <div className="mass-audience-switcher">
                            <button
                                type="button"
                                className={classnames('mass-switcher-pill', { active: selectedRadio === 'my_rds' })}
                                onClick={() => getRdList('my_rds')}
                                disabled={broadCastRDsListLoading}
                            >
                                <UserCheck size={15} className="mr-50" />
                                <span>My RDs</span>
                            </button>

                            <button
                                type="button"
                                className={classnames('mass-switcher-pill', { active: selectedRadio === 'all_rds' })}
                                onClick={() => getRdList('all_rds')}
                                disabled={broadCastRDsListLoading}
                            >
                                <Globe size={15} className="mr-50" />
                                <span>All RDs</span>
                            </button>

                            <button
                                type="button"
                                className={classnames('mass-switcher-pill', { active: selectedRadio === 'favourites' })}
                                onClick={() => getRdList('favourites')}
                                disabled={broadCastRDsListLoading}
                            >
                                <Folder size={15} className="mr-50" />
                                <span>From Favorites</span>
                            </button>

                            <button
                                type="button"
                                className={classnames('mass-switcher-pill', { active: selectedRadio === 'groups' })}
                                onClick={() => getRdList('groups')}
                                disabled={broadCastRDsListLoading}
                            >
                                <Sliders size={15} className="mr-50" />
                                <span>Filter RDs</span>
                                {activeFilterCount > 0 && (
                                    <span className="mass-pill-counter ml-50">{activeFilterCount}</span>
                                )}
                            </button>
                        </div>

                        {/* Favorite Folders Selector View */}
                        {showFav && (
                            <div className="mt-1 pt-1 border-top">
                                <span className="font-weight-bold text-dark d-block mb-1" style={{ fontSize: '13px' }}>
                                    Select a Favorite Folder:
                                </span>
                                <Row>
                                    {favFolderList && favFolderList.map((item, index) => (
                                        <Col xs={12} sm={6} md={4} lg={3} key={index} className="mb-1">
                                            <div
                                                className="mass-folder-card cursor-pointer"
                                                onClick={() => handleSelectedFolder(item.id, item.folder_name)}
                                            >
                                                <Folder size={20} className="text-primary mr-1" />
                                                <div className="text-truncate">
                                                    <h6 className="mb-0 text-truncate font-weight-bold" style={{ fontSize: '13px' }}>
                                                        {item?.folder_name}
                                                    </h6>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                        {/* Back to Folders button if folder selected */}
                        {backToFav && (
                            <div className="mt-1 pt-1 border-top d-flex align-items-center justify-content-between">
                                <button
                                    type="button"
                                    className="btn btn-link p-0 text-primary font-weight-bold d-flex align-items-center"
                                    onClick={() => getRdList('favourites')}
                                    disabled={broadCastRDsListLoading}
                                >
                                    <ArrowLeftCircle size={16} className="mr-50" /> Back to folders
                                </button>
                                <Badge color="light-primary" pill style={{ fontSize: '12px', padding: '5px 12px' }}>
                                    Folder: {favFolderTitle}
                                </Badge>
                            </div>
                        )}

                        {/* Expanded Filter Panel (When Filter RDs is selected) */}
                        {selectedRadio === 'groups' && (
                            <div className="mass-filter-panel mt-1 pt-1 border-top">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <div className="d-flex align-items-center">
                                        <Filter size={15} className="text-primary mr-50" />
                                        <span className="font-weight-bold text-dark" style={{ fontSize: '13px' }}>
                                            Filter Dietitians by Criteria
                                        </span>
                                    </div>
                                    {activeFilterCount > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 text-danger font-weight-bold d-flex align-items-center"
                                            onClick={handleResetAllFilters}
                                            style={{ fontSize: '12px' }}
                                        >
                                            <RotateCcw size={12} className="mr-25" /> Reset All Filters
                                        </button>
                                    )}
                                </div>

                                {/* Row 1: Location Filters (Country & State) */}
                                <Row className="mb-1">
                                    <Col xs={12} md={6}>
                                        <FormGroup className="mb-1 mb-md-0">
                                            <Label className="mass-filter-label">
                                                <MapPin size={13} className="mr-50 text-muted" /> Country:
                                            </Label>
                                            <Select
                                                isMulti
                                                isClearable
                                                placeholder="Select countries..."
                                                options={countryOptions}
                                                value={selectedCountries}
                                                onChange={(vals) => {
                                                    const updatedCountries = vals || []
                                                    setSelectedCountries(updatedCountries)
                                                    setSelectedStates([])
                                                    triggerGroupSearch({
                                                        countries: updatedCountries,
                                                        states: []
                                                    })
                                                }}
                                                className="react-select"
                                                classNamePrefix="select"
                                            />
                                        </FormGroup>
                                    </Col>

                                    <Col xs={12} md={6}>
                                        <FormGroup className="mb-0">
                                            <Label className="mass-filter-label">
                                                <MapPin size={13} className="mr-50 text-muted" /> State / Region:
                                            </Label>
                                            <Select
                                                isMulti
                                                isClearable
                                                placeholder="Select states..."
                                                options={filteredStateOptions}
                                                value={selectedStates}
                                                onChange={(vals) => {
                                                    const updatedStates = vals || []
                                                    setSelectedStates(updatedStates)
                                                    triggerGroupSearch({ states: updatedStates })
                                                }}
                                                className="react-select"
                                                classNamePrefix="select"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {/* Row 2: Reach & Years in Practice */}
                                <Row className="mb-1">
                                    <Col xs={12} md={6}>
                                        <FormGroup className="mb-1 mb-md-0">
                                            <Label className="mass-filter-label">
                                                <TrendingUp size={13} className="mr-50 text-muted" /> Reach (Followers):
                                            </Label>
                                            <Select
                                                isClearable
                                                placeholder="Select reach range..."
                                                options={REACH_OPTIONS}
                                                value={selectedReach}
                                                onChange={(val) => {
                                                    setSelectedReach(val)
                                                    triggerGroupSearch({ reach: val })
                                                }}
                                                className="react-select"
                                                classNamePrefix="select"
                                            />
                                        </FormGroup>
                                    </Col>

                                    <Col xs={12} md={6}>
                                        <FormGroup className="mb-0">
                                            <Label className="mass-filter-label">
                                                <Award size={13} className="mr-50 text-muted" /> Years in Practice:
                                            </Label>
                                            <Select
                                                isClearable
                                                placeholder="Select experience tier..."
                                                options={YEARS_IN_PRACTICE_OPTIONS}
                                                value={selectedYearsInPractice}
                                                onChange={(val) => {
                                                    setSelectedYearsInPractice(val)
                                                    triggerGroupSearch({ years: val })
                                                }}
                                                className="react-select"
                                                classNamePrefix="select"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {/* Row 3: Speciality, Industry, Personal Characteristics */}
                                <Row>
                                    <Col xs={12} md={4}>
                                        <div className="mass-checkbox-column">
                                            <div className="mass-column-header">
                                                <span className="font-weight-bold" style={{ fontSize: '12.5px' }}>Speciality</span>
                                                <Badge color="light-secondary" pill style={{ fontSize: '10px' }}>
                                                    {groupData?.speciality?.length || 0}
                                                </Badge>
                                            </div>
                                            <Input
                                                type="text"
                                                bsSize="sm"
                                                placeholder="Search speciality..."
                                                value={specialitySearch}
                                                onChange={(e) => setSpecialitySearch(e.target.value)}
                                                className="mass-column-search mb-1"
                                            />
                                            <div className="mass-column-scroll">
                                                {filteredSpecialities.map((brand, index) => {
                                                    const isChecked = (groupData.speciality || []).includes(brand.id)
                                                    return (
                                                        <CustomInput
                                                            key={`spec-${brand.id || index}`}
                                                            type="checkbox"
                                                            id={`mass-spec-${brand.id || index}`}
                                                            label={brand.label}
                                                            checked={isChecked}
                                                            onChange={(e) => handleGroupCheckboxToggle('speciality', e, brand)}
                                                            disabled={rdGroupListLoading}
                                                            className="mb-50 font-small-3"
                                                        />
                                                    )
                                                })}
                                                {filteredSpecialities.length === 0 && (
                                                    <span className="text-muted small">No specialities match search.</span>
                                                )}
                                            </div>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={4}>
                                        <div className="mass-checkbox-column">
                                            <div className="mass-column-header">
                                                <span className="font-weight-bold" style={{ fontSize: '12.5px' }}>Industry</span>
                                                <Badge color="light-secondary" pill style={{ fontSize: '10px' }}>
                                                    {groupData?.industry?.length || 0}
                                                </Badge>
                                            </div>
                                            <Input
                                                type="text"
                                                bsSize="sm"
                                                placeholder="Search industry..."
                                                value={industrySearch}
                                                onChange={(e) => setIndustrySearch(e.target.value)}
                                                className="mass-column-search mb-1"
                                            />
                                            <div className="mass-column-scroll">
                                                {filteredIndustries.map((brand, index) => {
                                                    const isChecked = (groupData.industry || []).includes(brand.id)
                                                    return (
                                                        <CustomInput
                                                            key={`ind-${brand.id || index}`}
                                                            type="checkbox"
                                                            id={`mass-ind-${brand.id || index}`}
                                                            label={brand.label}
                                                            checked={isChecked}
                                                            onChange={(e) => handleGroupCheckboxToggle('industry', e, brand)}
                                                            disabled={rdGroupListLoading}
                                                            className="mb-50 font-small-3"
                                                        />
                                                    )
                                                })}
                                                {filteredIndustries.length === 0 && (
                                                    <span className="text-muted small">No industries match search.</span>
                                                )}
                                            </div>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={4}>
                                        <div className="mass-checkbox-column">
                                            <div className="mass-column-header">
                                                <span className="font-weight-bold" style={{ fontSize: '12.5px' }}>Characteristics</span>
                                                <Badge color="light-secondary" pill style={{ fontSize: '10px' }}>
                                                    {groupData?.characteristics?.length || 0}
                                                </Badge>
                                            </div>
                                            <Input
                                                type="text"
                                                bsSize="sm"
                                                placeholder="Search characteristics..."
                                                value={charSearch}
                                                onChange={(e) => setCharSearch(e.target.value)}
                                                className="mass-column-search mb-1"
                                            />
                                            <div className="mass-column-scroll">
                                                {filteredCharacteristics.map((brand, index) => {
                                                    const isChecked = (groupData.characteristics || []).includes(brand.id)
                                                    return (
                                                        <CustomInput
                                                            key={`char-${brand.id || index}`}
                                                            type="checkbox"
                                                            id={`mass-char-${brand.id || index}`}
                                                            label={brand.label}
                                                            checked={isChecked}
                                                            onChange={(e) => handleGroupCheckboxToggle('characteristics', e, brand)}
                                                            disabled={rdGroupListLoading}
                                                            className="mb-50 font-small-3"
                                                        />
                                                    )
                                                })}
                                                {filteredCharacteristics.length === 0 && (
                                                    <span className="text-muted small">No characteristics match search.</span>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>

                    {/* Section 3: Recipients List & Real-Time Card Selection */}
                    <div className="mass-msg-card">
                        <div className="d-flex align-items-center justify-content-between mb-1 pb-50 border-bottom flex-wrap" style={{ gap: '10px' }}>
                            <div className="d-flex align-items-center">
                                <UserCheck size={16} className="text-primary mr-50" />
                                <h6 className="mb-0 font-weight-bold" style={{ fontSize: '13.5px', color: '#1e293b' }}>
                                    3. Select Recipients
                                </h6>
                                <span className="ml-1 text-muted small">
                                    ({broadCastRDsList?.length || 0} Found, {selectedBroadcastContacts?.length || 0} Selected)
                                </span>
                            </div>

                            {/* Actions on right: Select All & Search */}
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                                {broadCastRDsList?.length > 0 && (
                                    <CustomInput
                                        type="checkbox"
                                        id="mass-modal-check-all"
                                        label="Select All"
                                        checked={checkAllVal}
                                        onChange={handleCheckAll}
                                        className="font-weight-bold text-dark font-small-3 mr-1"
                                    />
                                )}

                                {broadCastRDsList?.length > 8 && (
                                    <div style={{ width: '200px' }}>
                                        <Input
                                            type="text"
                                            bsSize="sm"
                                            placeholder="Search by name..."
                                            value={searchRecipientTerm}
                                            onChange={(e) => setSearchRecipientTerm(e.target.value)}
                                            style={{ borderRadius: '6px', fontSize: '11.5px', height: '30px' }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Recipient Grid View */}
                        <div className="mass-recipients-container">
                            {(broadCastRDsListLoading || rdGroupListLoading) ? (
                                <div className="text-center py-4">
                                    <Spinner color="primary" size="sm" className="mr-50" />
                                    <span className="text-muted small">Loading dietitians...</span>
                                </div>
                            ) : (
                                <Row>
                                    {displayRecipients && displayRecipients.map((item, index) => {
                                        const isSelected = selectedBroadcastContacts?.includes(item.user_id)
                                        return (
                                            <Col xs={12} sm={6} md={6} lg={4} xl={3} key={item.user_id || index} className="mb-1">
                                                <div
                                                    className={classnames('mass-rd-item-card', { selected: isSelected })}
                                                    onClick={() => handleSelectBroadCastingContact(item)}
                                                >
                                                    <div className="d-flex align-items-center overflow-hidden flex-grow-1 mr-50">
                                                        <CustomInput
                                                            type="checkbox"
                                                            id={`mass-user-check-${item.user_id}`}
                                                            checked={isSelected}
                                                            disabled={true}
                                                            style={{ pointerEvents: 'none' }}
                                                            className="mr-50 flex-shrink-0"
                                                        />
                                                        <CustomAvatar
                                                            name={item?.name || item?.rd_name}
                                                            img={item?.profile_img || item?.profile_image}
                                                            imgHeight="36"
                                                            imgWidth="36"
                                                            className="mr-75 flex-shrink-0"
                                                        />
                                                        <div className="overflow-hidden flex-grow-1 mr-50" style={{ minWidth: 0 }}>
                                                            <div
                                                                className="mass-rd-name"
                                                                title={item?.name || item?.rd_name || 'Dietitian'}
                                                            >
                                                                {item?.name || item?.rd_name || 'Dietitian'}
                                                            </div>
                                                            <div
                                                                className="mass-rd-sub"
                                                                title={item?.folder_title || 'Registered Dietitian'}
                                                            >
                                                                {item?.folder_title || 'Registered Dietitian'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {item.is_favorite && (
                                                        <Heart size={15} color="#ef4444" fill="#ef4444" className="flex-shrink-0 ml-25" />
                                                    )}
                                                </div>
                                            </Col>
                                        )
                                    })}

                                    {/* Empty states */}
                                    {!broadCastRDsListLoading && !rdGroupListLoading && displayRecipients?.length === 0 && (
                                        <Col xs={12}>
                                            <div className="text-center py-4">
                                                {selectedRadio === 'groups' && activeFilterCount === 0 ? (
                                                    <div>
                                                        <Sliders size={28} className="text-muted mb-50" />
                                                        <p className="text-muted mb-0 font-weight-bold" style={{ fontSize: '13px' }}>
                                                            Select one or more filters above to find targeted dietitians.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <Users size={28} className="text-muted mb-50" />
                                                        <p className="text-muted mb-0 font-weight-bold" style={{ fontSize: '13px' }}>
                                                            No dietitians found matching your selection.
                                                        </p>
                                                        {selectedRadio === 'groups' && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm text-primary mt-50"
                                                                onClick={handleResetAllFilters}
                                                            >
                                                                Clear Filters
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            )}
                        </div>
                    </div>
                </ModalBody>

                {/* Modern Modal Footer */}
                <div className="mass-msg-footer d-flex align-items-center justify-content-between p-2 border-top">
                    <div className="d-flex align-items-center">
                        <span className="font-weight-bold text-dark" style={{ fontSize: '13px' }}>
                            Selected Recipients:
                        </span>
                        <Badge color="light-primary" pill className="ml-50" style={{ fontSize: '12px', padding: '4px 10px' }}>
                            {selectedBroadcastContacts.length} RD{selectedBroadcastContacts.length === 1 ? '' : 's'}
                        </Badge>
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                        <Button
                            color="secondary"
                            outline
                            size="sm"
                            className="mass-btn-cancel"
                            onClick={() => {
                                toggle()
                                setSelectedBraodcastContacts([])
                                setCheckAllVal(false)
                                setBroadCastRDsList([])
                                setMassMsgEditorState(null)
                                setBroadcastMessage('')
                                dispatch({ type: 'CLEAT_BROADCAST_LIST' })
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            size="sm"
                            className="mass-btn-send d-inline-flex align-items-center"
                            disabled={selectedBroadcastContacts.length === 0 || !broadcastMessage || broadcastMessage.trim() === ''}
                            onClick={() => setShowPreviewModal(true)}
                        >
                            <Send size={14} className="mr-50" />
                            <span>Review & Send Broadcast</span>
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Mass Message Confirmation & Preview Modal */}
            <Modal isOpen={showPreviewModal} toggle={() => setShowPreviewModal(false)} className="modal-lg mass-preview-modal" backdrop="static">
                <div className="mass-msg-header d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="mass-msg-icon-badge mr-1 flex-shrink-0">
                            <Send size={20} />
                        </div>
                        <div>
                            <h5 className="mb-0 font-weight-bold" style={{ color: '#1e293b', fontSize: '16px' }}>
                                Confirm Broadcast Send
                            </h5>
                            <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                Please review your broadcast message and recipients before sending.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="mass-msg-close-btn"
                        onClick={() => setShowPreviewModal(false)}
                        disabled={broadCastSendLoader}
                    >
                        <X size={18} />
                    </button>
                </div>

                <ModalBody className="p-2">
                    <Alert color="success" isOpen={Boolean(broadCastResStatus)} className="d-flex align-items-center">
                        <Check size={16} className="mr-1" />
                        <div>{broadCastResMsg || 'Broadcast sent successfully!'}</div>
                    </Alert>

                    {/* Message Preview */}
                    <FormGroup className="mb-2">
                        <Label className="font-weight-bold text-dark" style={{ fontSize: '13px' }}>
                            Broadcast Message Preview:
                        </Label>
                        <div
                            dangerouslySetInnerHTML={{ __html: broadcastMessage }}
                            className="mass-preview-message-box p-1.5 rounded border"
                            style={{ minHeight: '120px', maxHeight: '250px', overflowY: 'auto' }}
                        />
                    </FormGroup>

                    {/* Selected Recipients Preview */}
                    <FormGroup className="mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-50">
                            <Label className="font-weight-bold text-dark mb-0" style={{ fontSize: '13px' }}>
                                To ({selectedRds.length} Registered Dietitians):
                            </Label>
                        </div>
                        <div className="mass-preview-recipients-box p-1 rounded border" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                            {selectedRds.slice().sort((a, b) => (a || '').localeCompare(b || '')).map((name, i) => (
                                <Badge key={i} color="light-primary" pill className="mr-50 mb-50" style={{ fontSize: '11px', padding: '4px 10px' }}>
                                    {name}
                                </Badge>
                            ))}
                        </div>
                    </FormGroup>

                    {/* 1:1 Branching Notice */}
                    <Alert color="info" className="d-flex align-items-center py-75 px-1 mb-0" style={{ fontSize: '12px' }}>
                        <Info size={16} className="mr-1 flex-shrink-0" />
                        <div>
                            <strong>1:1 Reply Branching:</strong> When any dietitian replies, their response will automatically open as a private, direct conversation in your Inbox.
                        </div>
                    </Alert>
                </ModalBody>

                <ModalFooter className="p-1.5 border-top d-flex align-items-center justify-content-between">
                    <Button
                        color="secondary"
                        outline
                        size="sm"
                        className="mass-btn-cancel"
                        onClick={() => setShowPreviewModal(false)}
                        disabled={broadCastSendLoader}
                    >
                        Back to Edit
                    </Button>

                    <Button
                        color="primary"
                        size="sm"
                        className="mass-btn-send d-inline-flex align-items-center"
                        onClick={sendBroadcastMessage}
                        disabled={broadCastSendLoader || Boolean(broadCastResStatus)}
                    >
                        {broadCastSendLoader ? (
                            <>
                                <Spinner size="sm" className="mr-50" /> Sending Broadcast...
                            </>
                        ) : (
                            <>
                                <Send size={14} className="mr-50" /> Send Broadcast Now
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}

MassMessageModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    broadcastMessage: PropTypes.string.isRequired,
    setBroadcastMessage: PropTypes.func.isRequired,
    massMsgEditorState: PropTypes.any,
    setMassMsgEditorState: PropTypes.func.isRequired,
    selectedBroadcastContacts: PropTypes.array.isRequired,
    setSelectedBraodcastContacts: PropTypes.func.isRequired,
    selectedRds: PropTypes.array.isRequired,
    setSelectedRds: PropTypes.func.isRequired,
    broadCastRDsList: PropTypes.array.isRequired,
    setBroadCastRDsList: PropTypes.func.isRequired,
    broadCastRDsListLoading: PropTypes.bool.isRequired,
    setBroadCastRDsListLoading: PropTypes.func.isRequired,
    sendBroadcastMessage: PropTypes.func.isRequired,
    broadCastSendLoader: PropTypes.bool.isRequired,
    showPreviewModal: PropTypes.bool.isRequired,
    setShowPreviewModal: PropTypes.func.isRequired,
    broadCastResStatus: PropTypes.any,
    broadCastResMsg: PropTypes.string,
    brandsDashboardStoreSlice: PropTypes.object,
    rdGroupListLoading: PropTypes.bool,
    favFolderList: PropTypes.array,
    setFavFolderList: PropTypes.func.isRequired,
    showFav: PropTypes.bool.isRequired,
    setShowFav: PropTypes.func.isRequired,
    backToFav: PropTypes.bool.isRequired,
    setBackToFav: PropTypes.func.isRequired,
    favFolderTitle: PropTypes.string,
    setFavFolderTitle: PropTypes.func.isRequired,
    handleSelectedFolder: PropTypes.func.isRequired,
    getRdList: PropTypes.func.isRequired,
    selectedRadio: PropTypes.string.isRequired,
    setSelectedRadio: PropTypes.func.isRequired,
    selectedGroupLabels: PropTypes.array.isRequired,
    setSelectedGroupLabels: PropTypes.func.isRequired,
    groupData: PropTypes.object.isRequired,
    setGroupData: PropTypes.func.isRequired,
    checkAllVal: PropTypes.bool.isRequired,
    setCheckAllVal: PropTypes.func.isRequired,
    handleCheckAll: PropTypes.func.isRequired,
    handleSelectBroadCastingContact: PropTypes.func.isRequired,
    setIsOpenTempleteModal: PropTypes.func.isRequired,
    setOpenTemplatesModal: PropTypes.func.isRequired,
    setTemplateType: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    rdGroupList: PropTypes.func.isRequired,
    rdGroupListReset: PropTypes.func.isRequired
}

export default React.memo(MassMessageModal)
